import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaStar, FaQuoteLeft, FaChevronLeft, FaChevronRight, FaTrophy, FaArrowRight } from 'react-icons/fa';
import { testimonialsData } from '../data/testimonialsData';
import { Link } from 'react-router-dom';

const TestimonialsPage = () => {
  const [selected, setSelected] = useState(0);
  const tLength = testimonialsData.length;

  const handlePrev = () => setSelected(selected === 0 ? tLength - 1 : selected - 1);
  const handleNext = () => setSelected(selected === tLength - 1 ? 0 : selected + 1);

  const transition = { type: 'spring', duration: 2.5 };

  return (
    <div className="px-4 md:px-12 py-12 text-white overflow-hidden max-w-7xl mx-auto flex flex-col gap-16 min-h-screen">
      {/* ── 1. HEADER ── */}
      <motion.div
        className="text-center max-w-3xl mx-auto"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transition}
      >
        <span className="inline-block bg-red-600/15 border border-red-500/30 text-red-500 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4 shadow-lg">
          Real Results • 900+ Transformations
        </span>
        <div className="text-3xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight mb-4 leading-none">
          <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">MEMBER </span>
          <span className="text-red-500">STORIES</span>
        </div>
        <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
          Don't just take our word for it — hear from real members who transformed their bodies, mindset, and health at IDOL FITNESS ZONE.
        </p>
      </motion.div>

      {/* ── 2. FEATURED SPOTLIGHT CARD ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-gradient-to-b from-[#181a24]/95 to-[#12141c]/95 p-8 md:p-12 rounded-3xl border border-white/10 shadow-2xl relative overflow-hidden">
        {/* Ambient Glow */}
        <div className="absolute w-80 h-80 bg-red-600/15 blur-[160px] rounded-full top-0 left-0 pointer-events-none -z-0" />

        {/* Left Testimonial Content */}
        <div className="lg:col-span-7 flex flex-col gap-5 relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-red-600/20 text-red-500 flex items-center justify-center text-xl shadow-md">
              <FaQuoteLeft />
            </div>
            <div>
              <span className="text-[11px] font-black uppercase tracking-wider text-red-400 block">
                {testimonialsData[selected].branch}
              </span>
              <div className="flex items-center gap-1 text-amber-400 text-xs mt-0.5">
                {[...Array(testimonialsData[selected].rating)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={selected}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col gap-3"
            >
              <span className="text-xs sm:text-sm font-extrabold text-red-400 bg-red-600/10 px-3.5 py-1.5 rounded-xl border border-red-500/20 w-fit flex items-center gap-2">
                <FaTrophy className="text-red-500 text-xs" /> {testimonialsData[selected].achievement}
              </span>

              <p className="text-base sm:text-xl text-gray-200 leading-relaxed italic">
                "{testimonialsData[selected].review}"
              </p>

              <div className="pt-2">
                <span className="text-lg font-black text-white block uppercase tracking-wider">
                  {testimonialsData[selected].name}
                </span>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                  {testimonialsData[selected].status}
                </span>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Controls */}
          <div className="flex items-center gap-4 pt-4 border-t border-white/10">
            <button
              className="w-10 h-10 rounded-xl bg-white/5 hover:bg-red-600 text-white flex items-center justify-center transition-all cursor-pointer border border-white/10 shadow-md"
              onClick={handlePrev}
              aria-label="Previous Review"
            >
              <FaChevronLeft className="text-xs" />
            </button>

            <div className="flex items-center gap-2">
              {testimonialsData.map((_, i) => (
                <button
                  key={i}
                  className={`h-2 rounded-full cursor-pointer transition-all duration-300 ${
                    i === selected ? 'bg-red-600 w-7' : 'bg-white/20 w-2 hover:bg-white/40'
                  }`}
                  onClick={() => setSelected(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            <button
              className="w-10 h-10 rounded-xl bg-white/5 hover:bg-red-600 text-white flex items-center justify-center transition-all cursor-pointer border border-white/10 shadow-md"
              onClick={handleNext}
              aria-label="Next Review"
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        </div>

        {/* Right Athlete Image with Hero-Style Floating Frame */}
        <div className="lg:col-span-5 flex justify-center relative z-10">
          <div className="relative">
            {/* Background Glow Ring */}
            <div className="absolute inset-0 bg-red-600/30 rounded-3xl blur-2xl -z-10" />

            <AnimatePresence mode="wait">
              <motion.img
                key={selected}
                src={testimonialsData[selected].image}
                alt={testimonialsData[selected].name}
                className="w-64 h-80 sm:w-80 sm:h-96 object-cover rounded-3xl border-2 border-red-500/50 shadow-2xl"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
              />
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ── 3. ALL REVIEWS GRID ── */}
      <div className="flex flex-col gap-8">
        <div className="text-center">
          <span className="text-red-500 text-xs font-bold uppercase tracking-widest block mb-1">
            Community Voices
          </span>
          <h3 className="text-2xl sm:text-4xl font-black uppercase text-white">
            Feedback Across 3 Units
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonialsData.map((t, i) => (
            <motion.div
              key={i}
              className="bg-[#161820]/90 backdrop-blur-xl p-6 rounded-3xl border border-white/10 hover:border-red-500/40 transition-all flex flex-col justify-between shadow-xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.12, duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ y: -6 }}
            >
              <div>
                <div className="flex items-center gap-3.5 mb-4">
                  <img src={t.image} alt={t.name} className="w-12 h-12 rounded-2xl object-cover border-2 border-red-500" />
                  <div>
                    <span className="text-sm font-black text-white block uppercase">{t.name}</span>
                    <span className="text-[10px] text-red-400 font-semibold">{t.branch}</span>
                  </div>
                </div>

                <div className="flex text-amber-400 text-xs gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <FaStar key={j} />
                  ))}
                </div>

                <p className="text-gray-300 text-xs leading-relaxed italic">
                  "{t.review}"
                </p>
              </div>

              <div className="pt-4 mt-4 border-t border-white/10 flex items-center justify-between text-[11px]">
                <span className="text-gray-400">{t.achievement}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── 4. CTA ── */}
      <motion.div
        className="bg-gradient-to-r from-red-700 via-red-600 to-rose-700 p-8 sm:p-12 rounded-3xl shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-6 border border-white/20"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div>
          <h3 className="text-2xl sm:text-3xl font-black uppercase text-white mb-1">
            Write Your Own Success Story
          </h3>
          <p className="text-white/90 text-xs sm:text-sm">
            Join 900+ members in Coimbatore and start your transformation today.
          </p>
        </div>
        <Link
          to="/contact"
          className="bg-white hover:bg-gray-100 text-black font-black text-xs uppercase px-8 py-3.5 rounded-xl transition-all shadow-xl flex items-center gap-2 flex-shrink-0"
        >
          <span>Get Free Trial</span>
          <FaArrowRight className="text-xs" />
        </Link>
      </motion.div>
    </div>
  );
};

export default TestimonialsPage;
